import type { ClientOnboardingConfiguration, PublicOnboardingConfiguration } from './model.js';

export interface OnboardingReadiness {
  ready: boolean;
  publicLinkReady: boolean;
  locationsReady: boolean;
  profilesReady: boolean;
  missing: readonly ('Public link' | 'Locations' | 'Onboarding profiles')[];
}

/** Readiness is a setup checklist, never a health score. Departments are optional. */
export function evaluateOnboardingReadiness(input: {
  publicLinkActive: boolean;
  activeLocationCount: number;
  activeProfileCount: number;
}): OnboardingReadiness {
  const publicLinkReady = input.publicLinkActive;
  const locationsReady = input.activeLocationCount > 0;
  const profilesReady = input.activeProfileCount > 0;
  const missing: OnboardingReadiness['missing'][number][] = [];
  if (!publicLinkReady) missing.push('Public link');
  if (!locationsReady) missing.push('Locations');
  if (!profilesReady) missing.push('Onboarding profiles');
  return { ready: missing.length === 0, publicLinkReady, locationsReady, profilesReady, missing };
}

export function clientOnboardingReadiness(configuration: ClientOnboardingConfiguration): OnboardingReadiness {
  return evaluateOnboardingReadiness({
    publicLinkActive: configuration.clientActive && configuration.tokenActive,
    activeLocationCount: configuration.locations.filter((item) => item.active).length,
    activeProfileCount: configuration.profiles.filter((item) => item.active).length,
  });
}

/** A public configuration exists only after resolving an active Client and active token. */
export function publicOnboardingReadiness(configuration: PublicOnboardingConfiguration): OnboardingReadiness {
  return evaluateOnboardingReadiness({
    publicLinkActive: true,
    activeLocationCount: configuration.locations.length,
    activeProfileCount: configuration.profiles.length,
  });
}
